export type DroppedFile = { file: File; relativePath: string; handle?: FileSystemFileHandle };
export type TreeNode = { name: string; children: TreeNode[] };

type Entry = FileSystemEntry & { isFile: boolean; isDirectory: boolean; fullPath: string; name: string };
type FileEntry = Entry & { file: (ok: (f: File) => void, err: (e: unknown) => void) => void };
type DirEntry = Entry & { createReader: () => { readEntries: (ok: (es: Entry[]) => void, err: (e: unknown) => void) => void } };

function readAll(dir: DirEntry): Promise<Entry[]> {
  const reader = dir.createReader();
  const out: Entry[] = [];
  return new Promise((resolve, reject) => {
    const step = () =>
      reader.readEntries((es) => {
        if (es.length === 0) return resolve(out);
        out.push(...es);
        step();
      }, reject);
    step();
  });
}

async function walkEntry(entry: Entry, prefix: string, out: DroppedFile[]): Promise<void> {
  if (entry.isFile) {
    const file = await new Promise<File>((ok, err) => (entry as FileEntry).file(ok, err));
    if (file.name.startsWith('.') || file.name === 'Thumbs.db' || file.name === 'desktop.ini') return;
    out.push({ file, relativePath: prefix ? `${prefix}/${file.name}` : file.name });
  } else if (entry.isDirectory) {
    if (entry.name.startsWith('.')) return;
    const kids = await readAll(entry as DirEntry);
    for (const k of kids) await walkEntry(k, prefix ? `${prefix}/${entry.name}` : entry.name, out);
  }
}

/** §3.2 step 1 — expand a DataTransfer (files and folders) into `{file, relativePath}`. */
export async function walkDataTransfer(dt: DataTransfer): Promise<DroppedFile[]> {
  const out: DroppedFile[] = [];
  const items = dt.items ? Array.from(dt.items) : [];
  const entries = items.map((it) => (it.kind === 'file' && typeof it.webkitGetAsEntry === 'function' ? (it.webkitGetAsEntry() as Entry | null) : null));
  if (entries.some(Boolean)) {
    for (let i = 0; i < entries.length; i++) {
      const e = entries[i];
      if (e) await walkEntry(e, '', out);
      else {
        const f = items[i]?.getAsFile();
        if (f) out.push({ file: f, relativePath: f.name });
      }
    }
    return out;
  }
  for (const f of Array.from(dt.files)) out.push({ file: f, relativePath: (f as File & { webkitRelativePath?: string }).webkitRelativePath || f.name });
  return out;
}

/** Files from `<input webkitdirectory>` / `<input multiple>`. */
export function filesToDropped(files: FileList | File[], useRelativePaths: boolean): DroppedFile[] {
  return Array.from(files).map((f) => ({ file: f, relativePath: useRelativePaths ? (f as File & { webkitRelativePath?: string }).webkitRelativePath || f.name : f.name }));
}

/** Pure: build the directory tree (folder names only) for `POST /boards/batch`. */
export function buildTree(paths: readonly string[]): TreeNode[] {
  const root: TreeNode[] = [];
  for (const p of paths) {
    const segs = p.split('/').filter(Boolean);
    segs.pop(); // file name
    let level = root;
    for (const s of segs) {
      let n = level.find((x) => x.name === s);
      if (!n) {
        n = { name: s, children: [] };
        level.push(n);
      }
      level = n.children;
    }
  }
  return root;
}

export function dirOf(relativePath: string): string {
  const i = relativePath.lastIndexOf('/');
  return i < 0 ? '' : relativePath.slice(0, i);
}
